import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiActivity, FiZap, FiBox, FiKey, FiCpu, FiAlertCircle, FiPieChart, FiTrendingUp } = FiIcons;
import { mockUsageMetrics, mockClients, mockApps, mockProviders } from '../data/mockData';

const UsageProgress = ({ label, current, limit, unit = '' }) => {
  const percent = Math.min(100, (current / limit) * 100);
  const color = percent >= 90 ? 'bg-red-500' : percent >= 75 ? 'bg-amber-500' : 'bg-blue-600';
  
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-end">
        <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">{label}</p>
        <p className="text-[10px] font-mono text-slate-500">
          <span className="text-slate-900 font-bold">{current.toLocaleString()}</span> / {limit.toLocaleString()} {unit}
        </p>
      </div>
      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
        <div className={`h-full rounded-full transition-all duration-1000 ${color}`} style={{ width: `${percent}%` }}></div>
      </div>
    </div>
  );
};

const Usage = () => {
  const { global, ai, rateLimits } = mockUsageMetrics;

  return (
    <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Consumption & Quotas</h2>
          <p className="text-sm text-slate-500">Global and granular usage metrics across the gateway.</p>
        </div>
        <div className="px-4 py-2 bg-white border border-slate-200 rounded-xl shadow-sm text-xs font-bold flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> Telemetry Active
        </div>
      </div>

      {/* Global Quotas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-8">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <SafeIcon icon={FiPieChart} className="text-blue-500" /> Global Sending Quotas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <UsageProgress label="Daily Volume" current={global.daily} limit={global.dailyLimit} />
              <UsageProgress label="Monthly Volume" current={global.monthly} limit={global.monthlyLimit} />
              <UsageProgress label="Annual Volume" current={global.annual} limit={global.annualLimit} />
            </div>
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 space-y-4">
              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Rate Limit Policy</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-[10px] text-slate-500 font-bold uppercase">Per Minute</p>
                  <p className="text-lg font-bold text-slate-800">{rateLimits.perMinute}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] text-slate-500 font-bold uppercase">Per Hour</p>
                  <p className="text-lg font-bold text-slate-800">{rateLimits.perHour}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] text-slate-500 font-bold uppercase">Max Recipients</p>
                  <p className="text-lg font-bold text-slate-800">{rateLimits.maxRecipients}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] text-slate-500 font-bold uppercase">Block Mode</p>
                  <p className="text-sm font-black text-red-600 uppercase">Hard Block</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-900 rounded-2xl p-6 text-white space-y-6 shadow-xl">
          <h3 className="font-bold flex items-center gap-2">
            <SafeIcon icon={FiCpu} className="text-emerald-400" /> AI Assistant Usage
          </h3>
          <div className="space-y-8">
            <div className="space-y-2">
              <div className="flex justify-between items-end">
                <p className="text-xs font-bold text-slate-400 uppercase">Daily Chat Messages</p>
                <p className="text-xs font-bold">{ai.chatbotDaily} / {ai.chatbotLimit}</p>
              </div>
              <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 w-[42%]"></div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-end">
                <p className="text-xs font-bold text-slate-400 uppercase">Monthly LLM Tokens</p>
                <p className="text-xs font-bold">{ai.monthlyTokens.toLocaleString()} / {ai.tokenLimit.toLocaleString()}k</p>
              </div>
              <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 w-[64%]"></div>
              </div>
            </div>
            <div className="pt-4 border-t border-white/10">
              <p className="text-[10px] text-slate-500 italic leading-relaxed">
                Token estimates are based on average payload size across all AI-driven email classifications and summaries.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Granular Usage Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <SafeIcon icon={FiBox} className="text-slate-400" />
            <h3 className="font-bold text-slate-800 text-sm">App Level Utilization</h3>
          </div>
          <div className="p-4 space-y-4">
            {mockApps.map(app => (
              <div key={app.id} className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-2">
                <div className="flex justify-between">
                  <p className="text-xs font-bold text-slate-800">{app.name}</p>
                  <span className="text-[10px] font-bold text-blue-600 uppercase">Provider: {app.provider}</span>
                </div>
                <div className="h-1.5 w-full bg-slate-200 rounded-full">
                  <div className="h-full bg-blue-500 w-[45%] rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <SafeIcon icon={FiKey} className="text-slate-400" />
            <h3 className="font-bold text-slate-800 text-sm">API Key Consumption</h3>
          </div>
          <div className="p-4 space-y-4">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-2">
              <div className="flex justify-between">
                <p className="text-xs font-bold text-slate-800">Production Main</p>
                <span className="text-[10px] font-bold text-slate-500 uppercase">4,250 Sent</span>
              </div>
              <div className="h-1.5 w-full bg-slate-200 rounded-full">
                <div className="h-full bg-emerald-500 w-[85%] rounded-full"></div>
              </div>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-2 opacity-50">
              <div className="flex justify-between">
                <p className="text-xs font-bold text-slate-800">Staging Test</p>
                <span className="text-[10px] font-bold text-slate-500 uppercase">0 Sent</span>
              </div>
              <div className="h-1.5 w-full bg-slate-200 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Usage;